/**
 * Hooks local-first de HORARIO y ASISTENCIA (F3-A). Los datos llegan por los
 * streams por rol (my_cohorts_student/teacher, campus/org_admin_scope); la
 * escritura pasa por el write-path (mutations) o por REST (generate-sessions,
 * check-in, códigos, reporte, acknowledge).
 */
import type { Accessor } from "solid-js";
import {
  academyOrgQueryKeys,
  type AlertRow,
  type AttendanceReportRow,
  getAttendanceBySessionQuery,
  getCohortAttendanceReportQuery,
  getMyAttendanceQuery,
  getMySessionsBetweenQuery,
  getOpenAlertsByCohortQuery,
  getRoomsByOrgQuery,
  getSessionsByCohortQuery,
  getSlotsByCohortQuery,
  scheduleQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery } from "@space/shared/solid";
import type { AttendanceRecord, ClassSession, Room, ScheduleSlot } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();
  return { db, drizzle, userId, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** Franjas de horario semanal de una cohorte. */
export function useCohortSlots(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ScheduleSlot>({
    queryKey: () => scheduleQueryKeys.slotsByCohort(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getSlotsByCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Sesiones de clase de una cohorte. */
export function useCohortSessions(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<ClassSession>({
    queryKey: () => scheduleQueryKeys.sessionsByCohort(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getSessionsByCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MIS sesiones entre dos fechas (timetable personal). */
export function useMySessions(range: Accessor<{ from: string; to: string }>) {
  const base = useBase();
  return createPowerSyncQuery<ClassSession>({
    queryKey: () => scheduleQueryKeys.sessionsBetween(base.userId(), range().from, range().to),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const r = range();
      return db ? getMySessionsBetweenQuery(db, r.from, r.to) : null;
    },
    enabled: () => Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Asistencia de una sesión (una fila por alumno). */
export function useSessionAttendance(sessionId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<AttendanceRecord>({
    queryKey: () => scheduleQueryKeys.attendanceBySession(sessionId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = sessionId();
      return db && id ? getAttendanceBySessionQuery(db, id) : null;
    },
    enabled: () => Boolean(sessionId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MI asistencia (filas propias). */
export function useMyAttendance() {
  const base = useBase();
  return createPowerSyncQuery<AttendanceRecord>({
    queryKey: () => scheduleQueryKeys.myAttendance(base.userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = base.userId();
      return db && uid ? getMyAttendanceQuery(db, uid) : null;
    },
    enabled: () => Boolean(base.userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Resumen de asistencia por alumno de una cohorte (stats derivadas). */
export function useCohortAttendanceReport(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<AttendanceReportRow>({
    queryKey: () => scheduleQueryKeys.attendanceByCohort(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortAttendanceReportQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Aulas de una organización (para asignar sala a slots/sesiones). */
export function useRoomsByOrg(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Room>({
    queryKey: () => academyOrgQueryKeys.roomsByOrg(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getRoomsByOrgQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Alertas de riesgo ABIERTAS de una cohorte (staff). */
export function useCohortAlerts(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<AlertRow>({
    queryKey: () => scheduleQueryKeys.alertsByCohort(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getOpenAlertsByCohortQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
